import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Button } from '@/common/ui/Form/Button'
import { cropAvatarToFile, type CropAvatarToBlobOptions } from '@/common/ui/AvatarCropUpload/cropAvatarToBlob'
import './avatarCropUpload.css'

export type AvatarCropShape = 'circle' | 'square'

export type AvatarCropOutputSize = {
    width: number
    height: number
}

type CropRect = {
    x: number
    y: number
    w: number
    h: number
}

type CropDragState =
    | {
          kind: 'move'
          pointerId: number
          origin: CropRect
          startClientX: number
          startClientY: number
      }
    | {
          kind: 'resize-br'
          pointerId: number
          origin: CropRect
          startClientX: number
          startClientY: number
      }

const isAcceptedImageFile = (file: File, acceptAttr: string): boolean => {
    const tokens = acceptAttr
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
    if (tokens.length === 0) {
        return file.type.startsWith('image/')
    }

    return tokens.some(token => {
        if (token === 'image/*') {
            return file.type.startsWith('image/')
        }
        if (token.endsWith('/*')) {
            const main = token.slice(0, -2)
            return file.type.startsWith(`${main}/`)
        }
        if (token.startsWith('.')) {
            const ext = token.slice(1).toLowerCase()
            return file.name.toLowerCase().endsWith(`.${ext}`)
        }
        return file.type === token
    })
}

const MIN_CROP_PX = 32

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const fitImageDisplay = (containerW: number, containerH: number, naturalW: number, naturalH: number) => {
    if (containerW <= 0 || containerH <= 0 || naturalW <= 0 || naturalH <= 0) {
        return { dw: 0, dh: 0 }
    }
    const scale = Math.min(containerW / naturalW, containerH / naturalH)
    return { dw: naturalW * scale, dh: naturalH * scale }
}

const clampCropInside = (rect: CropRect, dw: number, dh: number): CropRect => {
    const w = Math.min(rect.w, dw)
    const h = Math.min(rect.h, dh)
    const x = clamp(rect.x, 0, Math.max(0, dw - w))
    const y = clamp(rect.y, 0, Math.max(0, dh - h))
    return { x, y, w, h }
}

const initialCropForAspect = (dw: number, dh: number, aspect: number): CropRect => {
    if (dw <= 0 || dh <= 0 || aspect <= 0) {
        return { x: 0, y: 0, w: 0, h: 0 }
    }

    let w: number
    let h: number
    if (dw / dh > aspect) {
        h = dh
        w = h * aspect
    } else {
        w = dw
        h = w / aspect
    }

    const x = (dw - w) / 2
    const y = (dh - h) / 2
    return { x, y, w, h }
}

const displayCropToSource = (crop: CropRect, naturalW: number, naturalH: number, dw: number, dh: number) => {
    const scaleX = naturalW / dw
    const scaleY = naturalH / dh
    const sx = crop.x * scaleX
    const sy = crop.y * scaleY
    const sw = crop.w * scaleX
    const sh = crop.h * scaleY
    return { sx, sy, sw, sh }
}

/** Максимальная ширина прямоугольника обрезки с фиксированным центром (cx, cy) и высотой w/aspectRatio, вписанного в [0,dw]×[0,dh]. */
const maxCenteredCropWidth = (cx: number, cy: number, dw: number, dh: number, aspectRatio: number) => {
    const byX = Math.min(2 * cx, 2 * (dw - cx))
    const byY = Math.min(2 * cy * aspectRatio, 2 * Math.max(0, dh - cy) * aspectRatio)
    return Math.max(0, Math.min(byX, byY))
}

export type AvatarCropUploadProps = {
    shape: AvatarCropShape
    outputSize: AvatarCropOutputSize
    accept?: string
    className?: string
    disabled?: boolean
    exportFileName?: string
    onExport: (file: File) => void | Promise<void | unknown>
    exportMimeType?: CropAvatarToBlobOptions['mimeType']
    jpegQuality?: number
    /** Подставить файл (например после drop снаружи виджета). */
    seedFile?: File | null
    onSeedFileConsumed?: () => void
}

export const AvatarCropUpload = ({
    shape,
    outputSize,
    accept = 'image/*',
    className,
    disabled,
    exportFileName = 'avatar',
    onExport,
    exportMimeType,
    jpegQuality,
    seedFile = null,
    onSeedFileConsumed,
}: AvatarCropUploadProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const cropDragRef = useRef<CropDragState | null>(null)

    const [objectUrl, setObjectUrl] = useState<string | null>(null)
    const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 })
    const [displaySize, setDisplaySize] = useState({ dw: 0, dh: 0 })
    const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, w: 0, h: 0 })
    const [isExporting, setIsExporting] = useState(false)
    const [isFileDragOver, setIsFileDragOver] = useState(false)

    const aspect = outputSize.width / outputSize.height

    const revokeObjectUrl = useCallback(() => {
        setObjectUrl(prev => {
            if (prev) {
                URL.revokeObjectURL(prev)
            }
            return null
        })
    }, [])

    useEffect(() => {
        return () => {
            revokeObjectUrl()
        }
    }, [revokeObjectUrl])

    const measure = useCallback(() => {
        const container = containerRef.current
        const image = imageRef.current
        if (!container || !image || !naturalSize.w || !naturalSize.h) {
            return
        }

        const { clientWidth: cw, clientHeight: ch } = container
        const { dw, dh } = fitImageDisplay(cw, ch, naturalSize.w, naturalSize.h)
        setDisplaySize({ dw, dh })
        setCrop(prev => {
            if (prev.w === 0 && prev.h === 0) {
                return clampCropInside(initialCropForAspect(dw, dh, aspect), dw, dh)
            }
            return clampCropInside(prev, dw, dh)
        })
    }, [aspect, naturalSize.h, naturalSize.w])

    useLayoutEffect(() => {
        measure()
    }, [measure, objectUrl])

    useEffect(() => {
        const ro = new ResizeObserver(() => measure())
        const el = containerRef.current
        if (el) {
            ro.observe(el)
        }
        return () => ro.disconnect()
    }, [measure, objectUrl])

    const loadImageFromFile = useCallback(
        (file: File) => {
            if (!isAcceptedImageFile(file, accept)) {
                return
            }
            revokeObjectUrl()
            const next = URL.createObjectURL(file)
            setObjectUrl(next)
            setNaturalSize({ w: 0, h: 0 })
            setDisplaySize({ dw: 0, dh: 0 })
            setCrop({ x: 0, y: 0, w: 0, h: 0 })
        },
        [accept, revokeObjectUrl]
    )

    const onSeedFileConsumedRef = useRef(onSeedFileConsumed)
    onSeedFileConsumedRef.current = onSeedFileConsumed

    useEffect(() => {
        if (!seedFile || disabled) {
            return
        }
        loadImageFromFile(seedFile)
        onSeedFileConsumedRef.current?.()
    }, [seedFile, disabled, loadImageFromFile])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) {
            return
        }
        loadImageFromFile(file)
        event.target.value = ''
    }

    const handleFileDragEnter = (event: React.DragEvent) => {
        if (disabled) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        const related = event.relatedTarget as Node | null
        if (!related || !event.currentTarget.contains(related)) {
            setIsFileDragOver(true)
        }
    }

    const handleFileDragLeave = (event: React.DragEvent) => {
        if (disabled) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        const related = event.relatedTarget as Node | null
        if (!related || !event.currentTarget.contains(related)) {
            setIsFileDragOver(false)
        }
    }

    const handleFileDragOver = (event: React.DragEvent) => {
        if (disabled) {
            event.preventDefault()
            event.dataTransfer.dropEffect = 'none'
            return
        }
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'copy'
    }

    const handleFileDrop = (event: React.DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        setIsFileDragOver(false)
        if (disabled) {
            return
        }
        const file = event.dataTransfer.files?.[0]
        if (!file) {
            return
        }
        loadImageFromFile(file)
    }

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const { naturalWidth: w, naturalHeight: h } = event.currentTarget
        setNaturalSize({ w, h })
    }

    const handleChooseClick = () => {
        if (disabled) {
            return
        }
        fileInputRef.current?.click()
    }

    const handleClearClick = () => {
        if (disabled) {
            return
        }
        revokeObjectUrl()
        setNaturalSize({ w: 0, h: 0 })
        setDisplaySize({ dw: 0, dh: 0 })
        setCrop({ x: 0, y: 0, w: 0, h: 0 })
    }

    const endCropDrag = () => {
        cropDragRef.current = null
    }

    const handleCropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (disabled || displaySize.dw === 0) {
            return
        }
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        cropDragRef.current = {
            kind: 'move',
            pointerId: event.pointerId,
            origin: crop,
            startClientX: event.clientX,
            startClientY: event.clientY,
        }
    }

    const handleResizePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        if (disabled || displaySize.dw === 0) {
            return
        }
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        cropDragRef.current = {
            kind: 'resize-br',
            pointerId: event.pointerId,
            origin: crop,
            startClientX: event.clientX,
            startClientY: event.clientY,
        }
    }

    const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
        const state = cropDragRef.current
        if (!state || state.pointerId !== event.pointerId || displaySize.dw === 0) {
            return
        }

        const { dw, dh } = displaySize
        const dx = event.clientX - state.startClientX
        const dy = event.clientY - state.startClientY

        if (state.kind === 'move') {
            const next = clampCropInside(
                {
                    x: state.origin.x + dx,
                    y: state.origin.y + dy,
                    w: state.origin.w,
                    h: state.origin.h,
                },
                dw,
                dh
            )
            setCrop(next)
            return
        }

        const origin = state.origin
        const cx = origin.x + origin.w / 2
        const cy = origin.y + origin.h / 2
        const maxWAllowed = maxCenteredCropWidth(cx, cy, dw, dh, aspect)
        const wFromDx = origin.w + 2 * dx
        const wFromDy = (origin.h + 2 * dy) * aspect
        let nextW = Math.abs(dy) > Math.abs(dx) * aspect ? wFromDy : wFromDx
        nextW = clamp(nextW, MIN_CROP_PX, Math.max(MIN_CROP_PX, maxWAllowed))
        const nextH = nextW / aspect
        const nextX = clamp(cx - nextW / 2, 0, Math.max(0, dw - nextW))
        const nextY = clamp(cy - nextH / 2, 0, Math.max(0, dh - nextH))
        setCrop({ x: nextX, y: nextY, w: nextW, h: nextH })
    }

    const handlePointerUp = (event: React.PointerEvent<HTMLElement>) => {
        const state = cropDragRef.current
        if (!state || state.pointerId !== event.pointerId) {
            return
        }
        event.currentTarget.releasePointerCapture(event.pointerId)
        endCropDrag()
    }

    const handlePointerCancel = (event: React.PointerEvent<HTMLElement>) => {
        const state = cropDragRef.current
        if (!state || state.pointerId !== event.pointerId) {
            return
        }
        try {
            event.currentTarget.releasePointerCapture(event.pointerId)
        } catch {
            /* ignore */
        }
        endCropDrag()
    }

    const handleExportClick = async () => {
        const image = imageRef.current
        if (!image || !naturalSize.w || !displaySize.dw || crop.w === 0) {
            return
        }

        const source = displayCropToSource(crop, naturalSize.w, naturalSize.h, displaySize.dw, displaySize.dh)
        const options: CropAvatarToBlobOptions = {
            outputWidth: outputSize.width,
            outputHeight: outputSize.height,
            shape,
            mimeType: exportMimeType ?? (shape === 'circle' ? 'image/png' : 'image/jpeg'),
            quality: jpegQuality,
        }

        setIsExporting(true)
        try {
            const file = await cropAvatarToFile(image, source, options, exportFileName)
            await onExport(file)
        } finally {
            setIsExporting(false)
        }
    }

    const handleKeyDownCropArea = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled || displaySize.dw === 0) {
            return
        }
        const step = event.shiftKey ? 12 : 4
        const { dw, dh } = displaySize
        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            setCrop(prev => clampCropInside({ ...prev, x: prev.x - step }, dw, dh))
            return
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            setCrop(prev => clampCropInside({ ...prev, x: prev.x + step }, dw, dh))
            return
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setCrop(prev => clampCropInside({ ...prev, y: prev.y - step }, dw, dh))
            return
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setCrop(prev => clampCropInside({ ...prev, y: prev.y + step }, dw, dh))
            return
        }
    }

    const shapeClass = shape === 'circle' ? 'avatar-crop-upload__mask--circle' : 'avatar-crop-upload__mask--square'
    const shapeHandleClass =
        shape === 'circle' ? 'avatar-crop-upload__crop-handle--circle' : 'avatar-crop-upload__crop-handle--square'

    return (
        <div className={cn('avatar-crop-upload', className)}>
            <input
                ref={fileInputRef}
                type='file'
                accept={accept}
                className='avatar-crop-upload__input'
                aria-label='Выбрать файл изображения для аватара'
                disabled={disabled}
                onChange={handleFileChange}
            />

            {!objectUrl ? (
                <div
                    role='region'
                    aria-label='Зона загрузки изображения. Можно перетащить файл сюда.'
                    onDragEnter={handleFileDragEnter}
                    onDragLeave={handleFileDragLeave}
                    onDragOverCapture={handleFileDragOver}
                    onDropCapture={handleFileDrop}
                    className={cn(
                        'avatar-crop-upload__dropzone',
                        isFileDragOver && !disabled && 'avatar-crop-upload__dropzone--active',
                        disabled && 'avatar-crop-upload__dropzone--disabled'
                    )}
                >
                    <span>Загрузите фото с устройства или перетащите сюда</span>
                    <Button type='button' size='lg' emphasis='accent' variant='outline' onClick={handleChooseClick}>
                        Выбрать файл
                    </Button>
                </div>
            ) : (
                <>
                    <div
                        ref={containerRef}
                        role='region'
                        aria-label='Превью изображения. Перетащите другой файл, чтобы заменить.'
                        onDragEnter={handleFileDragEnter}
                        onDragLeave={handleFileDragLeave}
                        onDragOverCapture={handleFileDragOver}
                        onDropCapture={handleFileDrop}
                        className={cn(
                            'avatar-crop-upload__preview',
                            isFileDragOver && !disabled && 'avatar-crop-upload__preview--drag-over'
                        )}
                    >
                        <div
                            className='avatar-crop-upload__stage'
                            style={{ width: displaySize.dw || undefined, height: displaySize.dh || undefined }}
                        >
                            <img
                                ref={imageRef}
                                src={objectUrl}
                                alt='Исходное изображение для обрезки'
                                className='avatar-crop-upload__image'
                                draggable={false}
                                onLoad={handleImageLoad}
                            />

                            {displaySize.dw > 0 && crop.w > 0 ? (
                                <>
                                    <div
                                        className={cn('avatar-crop-upload__mask', shapeClass)}
                                        style={{
                                            left: crop.x,
                                            top: crop.y,
                                            width: crop.w,
                                            height: crop.h,
                                        }}
                                        aria-hidden
                                    />
                                    <div
                                        role='button'
                                        tabIndex={0}
                                        aria-label='Область обрезки. Перетащите мышью или стрелками на клавиатуре.'
                                        className={cn('avatar-crop-upload__crop-handle', shapeHandleClass)}
                                        style={{
                                            left: crop.x,
                                            top: crop.y,
                                            width: crop.w,
                                            height: crop.h,
                                        }}
                                        onPointerDown={handleCropPointerDown}
                                        onPointerMove={handlePointerMove}
                                        onPointerUp={handlePointerUp}
                                        onPointerCancel={handlePointerCancel}
                                        onKeyDown={handleKeyDownCropArea}
                                    />
                                    <button
                                        type='button'
                                        aria-label='Изменить размер области обрезки'
                                        className={cn(
                                            'avatar-crop-upload__resize-handle',
                                            shape === 'circle'
                                                ? 'avatar-crop-upload__resize-handle--circle-right'
                                                : 'avatar-crop-upload__resize-handle--square-br'
                                        )}
                                        style={{
                                            left: crop.x + crop.w,
                                            top: shape === 'circle' ? crop.y + crop.h / 2 : crop.y + crop.h,
                                        }}
                                        onPointerDown={handleResizePointerDown}
                                        onPointerMove={handlePointerMove}
                                        onPointerUp={handlePointerUp}
                                        onPointerCancel={handlePointerCancel}
                                        disabled={disabled}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>

                    <div className='avatar-crop-upload__toolbar mt-[2rem] flex w-full shrink-0 items-center justify-between'>
                        <Button type='button' size='lg' emphasis='secondary' variant='ghost' onClick={handleClearClick}>
                            Сбросить
                        </Button>
                        <Button
                            type='button'
                            size='lg'
                            emphasis='accent'
                            variant='filled'
                            loading={isExporting}
                            disabled={disabled || crop.w === 0 || naturalSize.w === 0}
                            onClick={handleExportClick}
                        >
                            Обрезать и применить
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}
